import { useEffect, useMemo } from 'react';
import {
    ArchiveIcon,
    HouseIcon,
    ListFilterIcon,
    MailOpenIcon,
} from 'lucide-react';
import { Badge } from '@rocco/components/badge';
import { Checkbox } from '@rocco/components/checkbox';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { Spinner } from '@rocco/components/spinner';
import type { BuildHubFragmentFragment } from 'generated/graphql';
import type { BuildHubProject } from '../../types';
import { useConversationFilters } from '../../hooks/use-conversation-filters';

/**
 * A button and popover dropdown that allows the user to filter the chat inbox conversations.
 * @see {@link ChatInboxList}
 */
export function ChatInboxFilter({
    statusFilter,
    selectedProjects = [],
    onStatusFilterChange,
    onProjectsChange,
}: {
    statusFilter: 'all' | 'archived';
    selectedProjects?: BuildHubProject[];
    onStatusFilterChange?: (status: 'all' | 'archived') => void;
    onProjectsChange?: (projects: BuildHubProject[]) => void;
}) {
    const { filters, isLoading } = useConversationFilters();
    const projects = filters?.projects;
    const filterCount = useMemo(() => {
        return (statusFilter !== 'all' ? 1 : 0) + selectedProjects.length;
    }, [statusFilter, selectedProjects]);

    function handleProjectSelected(projectID: string, selected: boolean) {
        if (!projects) return;
        if (selected) {
            onProjectsChange?.(
                projects.filter(
                    (p) =>
                        p.ID === projectID ||
                        selectedProjects.some((sp) => sp.ID === p.ID)
                )
            );
        } else {
            onProjectsChange?.(
                selectedProjects.filter((p) => p.ID !== projectID)
            );
        }
    }

    // When the projects list changes, deselect selected projects not in the
    // new list. This makes sure there are no missing selected projects.
    useEffect(() => {
        onProjectsChange?.(
            projects?.filter((p) =>
                selectedProjects.some((sp) => sp.ID === p.ID)
            ) ?? []
        );
        // Do not pass `selectedProjectIDs` and `onProjectsChange` on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    className="relative flex size-12 items-center justify-center rounded-full hover:bg-black/10"
                    aria-label="Filter conversations"
                >
                    <ListFilterIcon className="size-6 text-black/70" />
                    {filterCount > 0 && (
                        <Badge
                            className="absolute -right-1 -top-1"
                            color="red"
                            size="centeredCompact"
                        >
                            {filterCount}
                        </Badge>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="flex max-h-[min(52rem,var(--radix-popover-content-available-height))] w-88 flex-col rounded-lg p-0"
                variant="shadcn"
                background="none"
                align="end"
                side="bottom"
                avoidCollisions={false}
            >
                <h3 className="relative flex items-center gap-2 p-4 text-3.5 leading-tight">
                    <span className="font-medium">Status</span>
                    {statusFilter !== 'all' && (
                        <button
                            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg px-2.5 py-1 hover:bg-black/10 focus-visible:outline focus-visible:outline-1 focus-visible:outline-input"
                            onClick={() => onStatusFilterChange?.('all')}
                        >
                            Reset
                        </button>
                    )}
                </h3>
                <RadioGroup
                    className="gap-0"
                    value={statusFilter}
                    onValueChange={(value) =>
                        onStatusFilterChange?.(value as 'all' | 'archived')
                    }
                >
                    <StatusOption
                        value="all"
                        label="All"
                        icon={<MailOpenIcon className="size-4.5" />}
                        isSelected={statusFilter === 'all'}
                        onClick={() => onStatusFilterChange?.('all')}
                    />
                    <StatusOption
                        value="archived"
                        label="Archived"
                        icon={<ArchiveIcon className="size-4.5" />}
                        isSelected={statusFilter === 'archived'}
                        onClick={() => onStatusFilterChange?.('archived')}
                    />
                </RadioGroup>
                <h3 className="relative flex items-center gap-2 p-4 text-3.5 leading-tight">
                    <span className="font-medium">Project</span>
                    {isLoading && <Spinner className="size-3" />}
                    {selectedProjects.length > 0 && (
                        <button
                            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg px-2.5 py-1 hover:bg-black/10 focus-visible:outline focus-visible:outline-1 focus-visible:outline-input"
                            onClick={() => onProjectsChange?.([])}
                        >
                            Clear
                        </button>
                    )}
                </h3>
                {projects && (
                    <ul
                        className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
                        role="listbox"
                    >
                        {projects.length > 0 ? (
                            projects.map((project) => (
                                <BuildHubOption
                                    key={project.ID}
                                    project={project}
                                    isSelected={selectedProjects.some(
                                        (sp) => sp.ID === project.ID
                                    )}
                                    onSelectedChange={(selected) =>
                                        handleProjectSelected(
                                            project.ID,
                                            selected
                                        )
                                    }
                                />
                            ))
                        ) : (
                            <li className="px-4 pb-4 text-4 leading-tight">
                                No projects to filter by
                            </li>
                        )}
                    </ul>
                )}
            </PopoverContent>
        </Popover>
    );
}

function StatusOption({
    value,
    label,
    icon,
    isSelected,
    onClick,
}: {
    value: 'all' | 'archived';
    label: string;
    icon: React.ReactNode;
    isSelected: boolean;
    onClick: () => void;
}) {
    return (
        <div
            className="flex cursor-pointer items-center gap-2 px-4 py-3 hover:bg-black/10 focus-visible:bg-black/5"
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            {icon}
            <span className="text-4 font-medium leading-tight">{label}</span>
            <RadioGroupItem
                className="pointer-events-none ml-auto"
                value={value}
                tabIndex={-1}
            />
        </div>
    );
}

function BuildHubOption({
    project,
    isSelected,
    onSelectedChange,
}: {
    project: BuildHubFragmentFragment;
    isSelected: boolean;
    onSelectedChange: (selected: boolean) => void;
}) {
    return (
        <li
            key={project.ID}
            className="flex cursor-pointer items-center gap-2 px-4 py-2 hover:bg-black/10 focus-visible:bg-black/5"
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            onClick={() => onSelectedChange(!isSelected)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectedChange(!isSelected);
                }
            }}
        >
            {project.ThumbnailFile?.Thumbnail.Desktop ? (
                <img
                    src={project.ThumbnailFile.Thumbnail.Desktop}
                    alt={project.Title}
                    className="size-8 rounded-full object-cover"
                />
            ) : (
                <div className="flex size-8 items-center justify-center rounded-full bg-white">
                    <HouseIcon className="size-5" />
                </div>
            )}
            <span className="min-w-0 flex-1 truncate text-4 font-medium leading-tight">
                {project.Title}
            </span>
            <Checkbox
                className="pointer-events-none"
                checked={isSelected}
                tabIndex={-1}
            />
        </li>
    );
}
